<script>
import { mdiPackageVariantClosed } from '@mdi/js'
import gql from 'graphql-tag'
import PartsOrderDetailsDialog from './parts_order_details_dialog.vue'
import DashboardSection from './dashboard_section.vue'

const PARTS_ORDERS_QUERY = gql`
    query DashboardPartsOrders($first: Int, $sort: SMSortQuery, $customerOnly: Boolean) {
        partsOrderBatchConnection(first: $first, sort: $sort, customerOnly: $customerOnly) {
            edges {
                cursor
                node {
                    rmaBatchId
                    createdAt
                    totalQty
                }
            }
            pageInfo {
                startCursor
                endCursor
                hasNextPage
                hasPreviousPage
            }
            totalCount
        }
    }
`

// Keep the history detail contract, including uid for Apollo's RMA cache key.
const PARTS_ORDER_DETAIL_QUERY = gql`
    query GetPartsOrderDetail($orderBatchId: String!) {
        getPartsOrderDetail(orderBatchId: $orderBatchId) {
            version
            uid
            userId
            metadata {
                health {
                    atCreateMoveAttachments {
                        applied
                        error
                    }
                    atCreateECN {
                        applied
                        error
                    }
                    atCreateShipping {
                        applied
                        error
                    }
                    atCreateRSPL {
                        applied
                        error
                    }
                    atCreateTPM {
                        applied
                        error
                    }
                    atCreateSendConfEmail {
                        applied
                        error
                    }
                }
                bouncer
                doa
                reportedIncorrectly
                rulesApplied {
                    name
                    index
                }
                overrides {
                    tpm
                    targetUser
                    skipEmail
                    shippingMethod
                    explanation
                    overriddenBy
                }
            }
            stage {
                created {
                    postedAt
                    postedBy
                    tpmCreatedAt
                    appliedPn
                    appliedQty
                }
                reported {
                    postedAt
                    postedBy
                    tpmCreatedAt
                }
                closed {
                    postedAt
                    postedBy
                    tpmCreatedAt
                }
                shipped {
                    postedAt
                    postedBy
                    tpmCreatedAt
                    shippedPn
                    shippedQty
                }
                cancelled {
                    postedAt
                    postedBy
                    tpmCreatedAt
                }
            }
            createdAt
            closedAt
            tracking {
                inbound {
                    uid
                    service
                    created
                    carrier
                    labelUrl
                    trackingNumber
                    trackingUrl
                    commercialInvoiceUrl
                    sent
                    received
                    eta
                    reference
                }
                outbound {
                    uid
                    service
                    created
                    carrier
                    labelUrl
                    trackingNumber
                    trackingUrl
                    commercialInvoiceUrl
                    sent
                    received
                    eta
                    reference
                }
                box {
                    uid
                    service
                    created
                    carrier
                    labelUrl
                    trackingNumber
                    trackingUrl
                    commercialInvoiceUrl
                    sent
                    received
                    eta
                    reference
                }
            }
            rmaBatchId
            rmaType
            unit {
                serial
                warrantyType
                endDeviceWarranty
                endBatteryWarranty
                endAccessoryWarranty
                shipDate
                model
                mfgModel
                deviceSpecs {
                    biosVersion
                    fullDescription
                    hdiVersion
                    hdiPart
                    imei
                    description
                    pn
                    mfgPn
                    motherboardPn
                    motherboardSerial
                }
                sla {
                    tat
                    shippingMethod
                    keepHd
                    zeroDefect
                    lcdUpgrade
                    saltFog
                    cosmeticUpgrade
                    fodUpgrade
                    emi
                }
                maxSMQty
            }
            smId
            category
            partSpec
            status
            closeReason
            partDescription
            tpm
            permissions {
                reimage
                biosFirmware
                userId
                userPassword
                biosPassword
            }
            customer {
                shipping {
                    address {
                        APO
                        company
                        line1
                        line2
                        city
                        state
                        country
                        postalCode
                        international
                    }
                    contact {
                        firstName
                        lastName
                        fullName
                        email
                        phone
                        company
                        position
                    }
                }
            }
        }
    }
`

export default {
    name: 'PartsOrdersSection',
    components: { DashboardSection, PartsOrderDetailsDialog },
    data() {
        return {
            orders: [],
            totalCount: 0,
            loading: true,
            fetchingOrders: false,
            error: '',
            detailsLoading: '',
            detailsError: '',
            selectedOrder: [],
            dialog: false
        }
    },
    computed: {
        section() {
            return {
                id: 'parts',
                title: 'Parts orders',
                icon: mdiPackageVariantClosed,
                description: 'Keep an eye on the parts that keep your team moving.',
                link: '/your-account/parts-orders-history',
                action: 'View orders',
                rows: this.orders.map((order) => ({
                    id: String(order.rmaBatchId),
                    title: `Order #${order.rmaBatchId}`,
                    detail: `Created ${this.formatDate(order.createdAt)}`,
                    status: order.totalQty == null ? 'Quantity unavailable' : `${order.totalQty} items`,
                    tone: 'neutral'
                })),
                footnote: this.loading
                    ? 'Loading your parts orders…'
                    : this.error
                    ? 'Parts orders are temporarily unavailable'
                    : `Showing ${this.orders.length} of ${this.totalCount} orders · Most recent first`
            }
        }
    },
    mounted() {
        this.fetchOrders()
    },
    methods: {
        formatDate(value) {
            if (!value) return 'date unavailable'
            const date = new Date(value)
            return Number.isNaN(date.getTime()) ? 'date unavailable' : date.toLocaleDateString()
        },
        emitSummary() {
            this.$emit('summary', {
                metric: this.loading ? '…' : this.error ? '—' : String(this.totalCount).padStart(2, '0'),
                metricLabel: 'Total parts orders'
            })
        },
        async fetchOrders() {
            if (this.fetchingOrders) return
            this.fetchingOrders = true
            this.loading = true
            this.error = ''
            this.emitSummary()
            try {
                // A fresh first page replaces relay pagination edges; don't slice a cached history connection.
                const { data } = await this.$apollo.query({
                    query: PARTS_ORDERS_QUERY,
                    variables: { first: 3, sort: { key: 'createdAt', descending: true }, customerOnly: true },
                    fetchPolicy: 'network-only'
                })
                const connection = data?.partsOrderBatchConnection
                if (!connection || !Array.isArray(connection.edges)) throw new Error('Parts orders unavailable')
                this.orders = connection.edges.map((edge) => edge?.node).filter((order) => order?.rmaBatchId)
                this.totalCount = Number(connection.totalCount) || 0
            } catch (error) {
                this.orders = []
                this.totalCount = 0
                this.error = 'Your parts orders could not be loaded. Please try again.'
            } finally {
                this.fetchingOrders = false
                this.loading = false
                this.emitSummary()
            }
        },
        async getBatchOrder(batchId) {
            if (this.detailsLoading) return
            this.detailsLoading = batchId
            this.detailsError = ''
            try {
                const { data } = await this.$apollo.query({
                    query: PARTS_ORDER_DETAIL_QUERY,
                    variables: { orderBatchId: batchId },
                    fetchPolicy: 'network-only'
                })
                const orders = data?.getPartsOrderDetail
                if (!Array.isArray(orders) || !orders.length || !orders[0]?.uid) {
                    throw new Error('Parts order details unavailable')
                }
                // The existing dialog dereferences shipping and tracking on the first line item.
                this.selectedOrder = orders.map((order) => ({
                    ...order,
                    customer: {
                        ...order.customer,
                        shipping: {
                            ...order.customer?.shipping,
                            address: order.customer?.shipping?.address || {},
                            contact: order.customer?.shipping?.contact || {}
                        }
                    },
                    tracking: order.tracking || {}
                }))
                this.dialog = true
            } catch (error) {
                this.detailsError = 'We could not open this order. Please try again or view your order history.'
            } finally {
                this.detailsLoading = ''
            }
        }
    }
}
</script>

<template>
    <dashboard-section
        :section="section"
        :loading="loading"
        :error="error"
        :details-loading="detailsLoading"
        :details-error="detailsError"
        loading-text="Loading your parts orders…"
        empty-text="No parts orders yet. Your orders will appear here after you place an order."
        selectable
        @retry="fetchOrders"
        @select="getBatchOrder"
    >
        <template v-slot:details>
            <parts-order-details-dialog
                v-if="dialog && selectedOrder.length"
                :value="dialog"
                :part-order="selectedOrder"
                @input="dialog = $event"
                @close="dialog = false"
            />
        </template>
    </dashboard-section>
</template>
